import { ChangeGroup } from 'store/interfaces';

export interface PDPActionStepChangeMerged {
  id: number;
  type: 'create' | 'update' | 'delete';
  payload: {
    name: string;
    isAchieved: boolean;
  };
  previousState?: {
    name: string;
    isAchieved: boolean;
  };
}

export interface PDPDevelopmentNeedChangeMerged {
  id: number;
  type: 'create' | 'update' | 'delete';
  payload: {
    name: string;
    deadline?: string | null;
    relatedArea: string;
    actionSteps?: PDPActionStepChangeMerged[];
  };
  previousState?: {
    id: number;
    name: string;
    deadline?: string | null;
    relatedArea: string;
    actionSteps?: PDPActionStepChangeMerged[];
  };
}

export interface PDPRequiredTrainingChangeMerged {
  id: number;
  type: 'create' | 'update' | 'delete';
  payload: {
    name: string;
    status: string;
  };
  previousState?: {
    id: number;
    name: string;
    status: string;
  };
}

export interface PDPChangeMerged {
  name?: string;
  supportRequired?: string;
  careerAspiration?: string;
  currentEnergyLevel?: number;
  currentStrengthAndImprovements?: string;
  motivationFactors?: string;
  developmentNeeds?: PDPDevelopmentNeedChangeMerged[];
  requiredTrainings?: PDPRequiredTrainingChangeMerged[];
  previousState?: {
    name?: string;
    supportRequired?: string;
    careerAspiration?: string;
    currentEnergyLevel?: number;
    currentStrengthAndImprovements?: string;
    motivationFactors?: string;
  };
}

export default function mergePDPUpdateChanges(
  changeGroups: ChangeGroup[],
): PDPChangeMerged {
  const changeGroup = changeGroups[0];
  const payload = changeGroup?.dataChangePayload || {};
  const previousState = changeGroup?.previousState || {};

  const merged: PDPChangeMerged = {
    ...payload,
    previousState: previousState || undefined,
  };

  const payloadDevelopmentNeeds = payload?.developmentNeeds || [];
  const previousStateDevelopmentNeeds = previousState?.developmentNeeds || [];
  const developmentNeeds: PDPDevelopmentNeedChangeMerged[] = [];

  previousStateDevelopmentNeeds.forEach((psdn: any) => {
    const prevStateData = psdn.previousState || psdn;
    const payloadNeedIndex = payloadDevelopmentNeeds.findIndex(
      (dn: any) => dn?.id === psdn.id,
    );

    if (payloadNeedIndex !== -1) {
      const payloadNeed = payloadDevelopmentNeeds[payloadNeedIndex];
      const payloadData = payloadNeed.payload || payloadNeed;
      const mergedNeed: PDPDevelopmentNeedChangeMerged = {
        id: psdn.id,
        type: 'update',
        payload: {
          name: payloadData.name || prevStateData.name || '',
          deadline: payloadData.deadline ?? prevStateData.deadline ?? null,
          relatedArea:
            payloadData.relatedArea || prevStateData.relatedArea || '',
          actionSteps: [],
        },
        previousState: {
          id: psdn.id,
          name: prevStateData.name || '',
          deadline: prevStateData.deadline ?? null,
          relatedArea: prevStateData.relatedArea || '',
          actionSteps: [],
        },
      };

      const payloadActionSteps = payloadData.actionSteps || [];
      const previousStateActionSteps = prevStateData.actionSteps || [];

      previousStateActionSteps.forEach((psas: any) => {
        const prevStepData = psas.previousState || psas;
        const payloadStepIndex = payloadActionSteps.findIndex(
          (as: any) => as?.id === psas.id,
        );

        if (payloadStepIndex !== -1) {
          const payloadStep = payloadActionSteps[payloadStepIndex];
          const payloadStepData = payloadStep.payload || payloadStep;
          mergedNeed.payload.actionSteps!.push({
            id: psas.id,
            type: 'update',
            payload: {
              name: payloadStepData.name || prevStepData.name || '',
              isAchieved:
                payloadStepData.isAchieved ?? prevStepData.isAchieved ?? false,
            },
            previousState: {
              name: prevStepData.name || '',
              isAchieved: prevStepData.isAchieved ?? false,
            },
          });
        } else {
          mergedNeed.payload.actionSteps!.push({
            id: psas.id,
            type: 'delete',
            payload: {
              name: prevStepData.name || '',
              isAchieved: prevStepData.isAchieved ?? false,
            },
            previousState: {
              name: prevStepData.name || '',
              isAchieved: prevStepData.isAchieved ?? false,
            },
          });
        }
      });

      payloadActionSteps.forEach((pas: any) => {
        if (!previousStateActionSteps.find((psas: any) => psas.id === pas.id)) {
          const payloadStepData = pas.payload || pas;
          mergedNeed.payload.actionSteps!.push({
            id: pas.id,
            type: 'create',
            payload: {
              name: payloadStepData.name || '',
              isAchieved: payloadStepData.isAchieved ?? false,
            },
          });
        }
      });

      developmentNeeds.push(mergedNeed);
    } else {
      const deletedNeed: PDPDevelopmentNeedChangeMerged = {
        id: psdn.id,
        type: 'delete',
        payload: {
          name: prevStateData.name || '',
          deadline: prevStateData.deadline ?? null,
          relatedArea: prevStateData.relatedArea || '',
          actionSteps: [],
        },
        previousState: {
          id: psdn.id,
          name: prevStateData.name || '',
          deadline: prevStateData.deadline ?? null,
          relatedArea: prevStateData.relatedArea || '',
          actionSteps: [],
        },
      };

      const previousStateActionSteps = prevStateData.actionSteps || [];
      previousStateActionSteps.forEach((psas: any) => {
        const prevStepData = psas.previousState || psas;
        deletedNeed.payload.actionSteps!.push({
          id: psas.id,
          type: 'delete',
          payload: {
            name: prevStepData.name || '',
            isAchieved: prevStepData.isAchieved ?? false,
          },
          previousState: {
            name: prevStepData.name || '',
            isAchieved: prevStepData.isAchieved ?? false,
          },
        });
      });

      developmentNeeds.push(deletedNeed);
    }
  });

  payloadDevelopmentNeeds.forEach((pdn: any) => {
    if (
      !previousStateDevelopmentNeeds.find((psdn: any) => psdn.id === pdn.id)
    ) {
      const payloadData = pdn.payload || pdn;
      const createdNeed: PDPDevelopmentNeedChangeMerged = {
        id: pdn.id,
        type: 'create',
        payload: {
          name: payloadData.name || '',
          deadline: payloadData.deadline ?? null,
          relatedArea: payloadData.relatedArea || '',
          actionSteps: [],
        },
      };

      const payloadActionSteps = payloadData.actionSteps || [];
      payloadActionSteps.forEach((pas: any) => {
        const payloadStepData = pas.payload || pas;
        createdNeed.payload.actionSteps!.push({
          id: pas.id,
          type: 'create',
          payload: {
            name: payloadStepData.name || '',
            isAchieved: payloadStepData.isAchieved ?? false,
          },
        });
      });

      developmentNeeds.push(createdNeed);
    }
  });

  merged.developmentNeeds = developmentNeeds;

  const payloadRequiredTrainings = payload?.requiredTrainings || [];
  const previousStateRequiredTrainings = previousState?.requiredTrainings || [];
  const requiredTrainings: PDPRequiredTrainingChangeMerged[] = [];

  previousStateRequiredTrainings.forEach((psrt: any) => {
    const prevTrainingData = psrt.previousState || psrt;
    const payloadTrainingIndex = payloadRequiredTrainings.findIndex(
      (rt: any) => rt?.id === psrt.id,
    );

    if (payloadTrainingIndex !== -1) {
      const payloadTraining = payloadRequiredTrainings[payloadTrainingIndex];
      const payloadTrainingData = payloadTraining.payload || payloadTraining;
      requiredTrainings.push({
        id: psrt.id,
        type: 'update',
        payload: {
          name: payloadTrainingData.name || prevTrainingData.name || '',
          status: payloadTrainingData.status || prevTrainingData.status || '',
        },
        previousState: {
          id: psrt.id,
          name: prevTrainingData.name || '',
          status: prevTrainingData.status || '',
        },
      });
    } else {
      requiredTrainings.push({
        id: psrt.id,
        type: 'delete',
        payload: {
          name: prevTrainingData.name || '',
          status: prevTrainingData.status || '',
        },
        previousState: {
          id: psrt.id,
          name: prevTrainingData.name || '',
          status: prevTrainingData.status || '',
        },
      });
    }
  });

  payloadRequiredTrainings.forEach((prt: any) => {
    if (
      !previousStateRequiredTrainings.find((psrt: any) => psrt.id === prt.id)
    ) {
      const payloadTrainingData = prt.payload || prt;
      requiredTrainings.push({
        id: prt.id,
        type: 'create',
        payload: {
          name: payloadTrainingData.name || '',
          status: payloadTrainingData.status || '',
        },
      });
    }
  });

  merged.requiredTrainings = requiredTrainings;

  return merged;
}
